import { SystemPermissions, type PermissionViewModel, type SystemPermissionKey } from "@nexus/contracts";

import type { DashboardMode } from "../types/dashboard.types";
import type { ExploreDataScope } from "./explore-api-prefix";

/** Missing or unknown product line (cached sessions) keeps today's both-modes behavior. */
const DEFAULT_EXPLORE_PRODUCT_LINE = "CORE_AND_COMP";

export interface ExploreModeAccess {
  showToggle: boolean;
  lockedMode: DashboardMode | null;
}

export function resolveExploreModeAccess(
  productLine: string | null | undefined,
  canUseMyBrand: boolean,
): ExploreModeAccess {
  const line =
    productLine === "CORE" || productLine === "COMP" || productLine === "CORE_AND_COMP"
      ? productLine
      : DEFAULT_EXPLORE_PRODUCT_LINE;

  if (line === "CORE") return { showToggle: false, lockedMode: "my-brand" };
  if (line === "COMP") return { showToggle: false, lockedMode: "competitors" };
  return canUseMyBrand
    ? { showToggle: true, lockedMode: null }
    : { showToggle: false, lockedMode: "competitors" };
}

/** Saving competitor defaults is a write. Page view does not include it. */
export const EXPLORE_COMPETITOR_DEFAULTS_PUT_KEY: Record<ExploreDataScope, SystemPermissionKey> = {
  dashboard: "DASHBOARD_PUT_COMPETITOR_FILTER_DEFAULTS",
  overview: "OVERVIEW_PUT_COMPETITOR_FILTER_DEFAULTS",
  drilldown: "DRILLDOWN_PUT_COMPETITOR_FILTER_DEFAULTS",
};

/** Page grant that lets Core & Comp use both Explore modes. */
export const EXPLORE_MY_BRAND_PERMISSION_KEYS: Record<ExploreDataScope, readonly SystemPermissionKey[]> = {
  dashboard: ["DASHBOARD_VIEW_PAGE"],
  overview: ["OVERVIEW_VIEW_PAGE"],
  drilldown: ["DRILLDOWN_VIEW_PAGE"],
};

export function canUseExploreMyBrand(
  scope: ExploreDataScope,
  permissions: Record<SystemPermissionKey, PermissionViewModel>,
): boolean {
  return EXPLORE_MY_BRAND_PERMISSION_KEYS[scope].every((key) => permissions[key]?.isOn === true);
}

export function tenantGrantsExploreMyBrand(
  scope: ExploreDataScope,
  granted: ReadonlyArray<{ type: string; method: string; route: string }>,
): boolean {
  return EXPLORE_MY_BRAND_PERMISSION_KEYS[scope].every((key) => {
    const def = SystemPermissions[key];
    return granted.some(
      (perm) => perm.type === def.type && perm.method === def.method && perm.route === def.route,
    );
  });
}
